import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserProviderAssignments } from "@/components/admin/UserProviderAssignments";
import { AdminUsers } from "@/components/admin/AdminUsers";
import { ProvidersManagement } from "@/components/admin/ProvidersManagement";
import CustomerManagement from "@/components/admin/CustomerManagement";
import SpecialtiesManagement from "@/components/admin/SpecialtiesManagement";
import FinanceManagement from "@/components/admin/FinanceManagement";
import { ErrorLogsPanel } from "@/components/admin/ErrorLogsPanel";
import OutreachManagement from "@/components/admin/OutreachManagement";
import AdminHeader from "@/components/admin/AdminHeader";
import AdminNavigation from "@/components/admin/AdminNavigation";
import WebhookTester from "@/components/admin/WebhookTester";
import EnhancedWebhookManagement from "@/components/admin/EnhancedWebhookManagement";
import FeatureOrderManagement from "@/components/admin/FeatureOrderManagement";
import EmailLogsManagement from "@/components/admin/EmailLogsManagement";
import EmailTemplatesManagement from "@/components/admin/EmailTemplatesManagement";
import ReviewRequestsManagement from "@/components/admin/ReviewRequestsManagement";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";
import { Shield } from "lucide-react";

const Admin = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [activeSection, setActiveSection] = useState("assignments");

  // Check if current user is admin using the new function
  const { data: adminCheck, isLoading } = useQuery({
    queryKey: ['admin-check', user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data, error } = await supabase
        .rpc('check_admin_access');
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    setIsAdmin(adminCheck || false);
  }, [adminCheck]);

  // Make current user admin (for initial setup)
  const makeCurrentUserAdmin = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('admin_users')
        .insert({
          user_id: user.id,
          role: 'admin'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "You are now an admin user.",
      });
      
      setIsAdmin(true);
    } catch (error) {
      console.error('Error making user admin:', error);
      toast({
        title: "Error",
        description: "Failed to make you an admin. You may already be an admin.",
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case "assignments":
        return <UserProviderAssignments />;
      case "providers":
        return <ProvidersManagement />;
      case "customers":
        return <CustomerManagement />;
      case "specialties":
        return <SpecialtiesManagement />;
      case "finance":
        return <FinanceManagement />;
      case "admins":
        return <AdminUsers />;
      case "outreach":
        return <OutreachManagement />;
      case "feature-order":
        return <FeatureOrderManagement />;
      case "errors":
        return <ErrorLogsPanel />;
      case "webhooks":
        return <WebhookTester />;
      case "webhook-management":
        return <EnhancedWebhookManagement />;
      case "email-logs":
        return <EmailLogsManagement />;
      case "email-templates":
        return <EmailTemplatesManagement />;
      case "review-requests":
        return <ReviewRequestsManagement />;
      default:
        return <UserProviderAssignments />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
              <p className="text-gray-600">Please log in to access the admin area.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <Shield className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <CardTitle className="text-xl">Admin Access Required</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-center">You need admin privileges to access this area.</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  If this is the initial setup and you should be an admin, click the button below.
                </p>
              </div>
              <Button onClick={makeCurrentUserAdmin} className="w-full">
                <Shield className="h-4 w-4 mr-2" />
                Make Me Admin (Initial Setup)
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage users, providers, and system settings</p>
          </div>
        </div>

        {/* Navigation and Content */}
        <div className="space-y-6">
          {/* Navigation Bar */}
          <AdminNavigation
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />

          {/* Main Content */}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
